import { inject, Injectable, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { APP_LANGS, AppLang, LANG_STORAGE_KEY, resolveInitialLang, writeSharedLocaleCookie } from '../i18n/language';

/**
 * Fuente única del idioma activo de la app. Expone un `signal` reactivo,
 * persiste la elección en localStorage y la sincroniza con Transloco (textos)
 * y con `<html lang>`. El LOCALE_ID de Angular (fechas/moneda de los pipes) se
 * fija en bootstrap con el mismo valor inicial; al cambiar de idioma la app se
 * recarga (ver setLanguage), así textos y formatos quedan todos en el nuevo.
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {
    private transloco = inject(TranslocoService);

    private readonly _lang = signal<AppLang>(resolveInitialLang());
    /** Idioma activo (reactivo). */
    readonly lang = this._lang.asReadonly();

    constructor() {
        // Aplica el idioma inicial (persistido / navegador) al arrancar.
        this.apply(this._lang());
    }

    setLanguage(lang: AppLang): void {
        if (lang === this._lang()) return;
        this._lang.set(lang);
        try {
            localStorage.setItem(LANG_STORAGE_KEY, lang);
        } catch {
            /* localStorage no disponible: el idioma vive solo en memoria esta sesión */
        }
        writeSharedLocaleCookie(lang);
        this.apply(lang);
        // Muchas pantallas arman sus textos con translate() una sola vez
        // (menús, pasos, columnas, opciones): al cambiar en caliente quedaban
        // en el idioma anterior, mezclados con el nuevo. Recargar las vuelve a
        // armar todas en el idioma elegido, igual que las fechas y números
        // (LOCALE_ID se fija al arrancar).
        try {
            location.reload();
        } catch {
            /* entorno sin location (pruebas): queda el cambio en caliente */
        }
    }

    /** Rota al siguiente idioma disponible (usado por el switcher del shell). */
    toggle(): void {
        const next = APP_LANGS[(APP_LANGS.indexOf(this._lang()) + 1) % APP_LANGS.length];
        this.setLanguage(next);
    }

    /** BCP-47 para APIs de formato (Intl / toLocaleString). */
    get locale(): string {
        switch (this._lang()) {
            case 'en':
                return 'en-US';
            case 'pt':
                return 'pt-BR';
            default:
                return 'es-CO';
        }
    }

    private apply(lang: AppLang): void {
        this.transloco.setActiveLang(lang);
        try {
            document.documentElement.lang = lang;
        } catch {
            /* SSR / entorno sin document */
        }
    }
}
